import React from 'react';
import { Tag } from 'primereact/tag';
import { ExamStudentEligibility } from '../../../../../../api/types/programs/course/exams/exam-sessions-enrollment-entity';
import { SessionType } from '../../../../../../api/types/programs/course/exams/response/exam-metadata';
import BubbleText, {
  BubbleType,
} from '../../../../../../component/bubble-text/bouble-text';
import { FlattenedExamSession } from '../../types/exam-types';

// Section header component
export const SectionHeader: React.FC<{
  title: string;
  icon: string;
  count: number;
  accentColor: string;
}> = ({ title, icon, count, accentColor }) => (
  <div
    style={{
      padding: '16px 20px',
      borderBottom: '1px solid #E5E7EB',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      background: `linear-gradient(135deg, ${accentColor}15 0%, transparent 100%)`,
    }}
  >
    <i
      className={icon}
      style={{
        fontSize: '24px',
        color: accentColor,
      }}
    />
    <div>
      <h3
        style={{
          margin: 0,
          fontSize: '18px',
          fontWeight: 'bold',
          color: '#041E49',
        }}
      >
        {title}
      </h3>
      <span style={{ fontSize: '14px', color: '#666' }}>{count} מבחנים</span>
    </div>
  </div>
);

// Empty state component
export const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div
    style={{
      textAlign: 'center',
      padding: '40px',
      color: '#888',
    }}
  >
    <i
      className="pi pi-inbox"
      style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}
    />
    <p style={{ margin: 0 }}>{message}</p>
  </div>
);

// Section wrapper component
export const SectionWrapper: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => (
  <div
    style={{
      marginBottom: '32px',
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #E5E7EB',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    }}
  >
    {children}
  </div>
);

// Template helper functions
export const getSessionTypeLabel = (sessionType: SessionType): string => {
  switch (sessionType) {
    case SessionType.FIRST_SESSION:
      return "מועד א'";
    case SessionType.SECOND_SESSION:
      return "מועד ב'";
    default:
      return sessionType;
  }
};

export const sessionTypeTemplate = (session: FlattenedExamSession) => {
  return getSessionTypeLabel(session.sessionType);
};

export const sessionDateTemplate = (session: FlattenedExamSession) => {
  if (!session.sessionDate) return 'טרם נקבע';
  return new Date(session.sessionDate).toLocaleDateString('he-IL');
};

export const registrationDatesTemplate = (session: FlattenedExamSession) => {
  const start = new Date(session.registrationStartDate).toLocaleDateString(
    'he-IL',
  );
  const end = new Date(session.registrationEndDate).toLocaleDateString('he-IL');
  return `${start} - ${end}`;
};

export const locationTemplate = (session: FlattenedExamSession) => {
  if (!session.scheduling || session.scheduling.length === 0) return '-';
  const schedule = session.scheduling[0];
  const parts: string[] = [];
  if (schedule.buildingName) parts.push(schedule.buildingName);
  if (schedule.className) parts.push(schedule.className);
  return parts.length > 0 ? parts.join(' - ') : '-';
};

export const timeTemplate = (session: FlattenedExamSession) => {
  if (!session.scheduling || session.scheduling.length === 0) return '-';
  const schedule = session.scheduling[0];
  if (!schedule.startTime || !schedule.endTime) return '-';

  const startTime = new Date(schedule.startTime).toLocaleTimeString('he-IL', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const endTime = new Date(schedule.endTime).toLocaleTimeString('he-IL', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${startTime} - ${endTime}`;
};

export const eligibilityTemplate = (session: FlattenedExamSession) => {
  switch (session.eligibility) {
    case ExamStudentEligibility.ELIGIBILITY:
      return <BubbleText text="זכאי" type={BubbleType.GREEN} />;
    case ExamStudentEligibility.EXCLUDED:
      return <BubbleText text="מוחרג" type={BubbleType.YELLOW} />;
    case ExamStudentEligibility.NOT_ELIGIBILITY:
      return <BubbleText text="לא זכאי" type={BubbleType.RED} />;
    default:
      return '-';
  }
};

export const attendedTemplate = (session: FlattenedExamSession) => {
  switch (session.attended) {
    case 'attended':
      return <Tag value="נכח" severity="success" />;
    case 'not_attended':
      return <Tag value="לא נכח" severity="danger" />;
    case 'exempt':
      return <Tag value="פטור" severity="info" />;
    default:
      return <Tag value="טרם הוזן" severity="secondary" />;
  }
};

export const gradeTemplate = (session: FlattenedExamSession) => {
  if (session.grade !== null && session.grade !== undefined) {
    return (
      <span style={{ fontWeight: 'bold', fontSize: '16px' }}>
        {session.grade}
      </span>
    );
  }
  return <span style={{ color: '#888' }}>טרם פורסם</span>;
};
